import { useEffect, useCallback, useRef } from 'react'

/**
 * Preloads images by creating `new Image()` instances so the browser
 * fetches them into its HTTP cache before they are needed in the DOM.
 *
 * When `eager` is true (default), images are preloaded on mount.
 * When `eager` is false, call the returned `preload` function to
 * trigger preloading on user intent (e.g. hover/focus).
 */
export default function usePreloadImages (
  urls: string[],
  { eager = true }: { eager?: boolean } = {},
): { preload: () => void } {
  const key = urls.join(',')
  const hasPreloaded = useRef(false)
  const imagesRef = useRef<HTMLImageElement[]>([])

  const doPreload = useCallback(() => {
    if (typeof window === 'undefined' || urls.length === 0 || hasPreloaded.current) return
    hasPreloaded.current = true
    imagesRef.current = urls.map((url) => {
      const img = new Image()
      img.src = url
      return img
    })
  }, [key])

  useEffect(() => {
    if (eager) doPreload()
    return () => {
      imagesRef.current.forEach((image) => {
        Object.assign(image, { src: '' })
      })
      imagesRef.current = []
      hasPreloaded.current = false
    }
  }, [key, eager, doPreload])

  return { preload: doPreload }
}
